import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Tooltip, IconButton, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import { isVisualizador } from '../services/auth';

export default function Usuarios() {
    const soVisualizacao = isVisualizador();
    const [usuarios, setUsuarios] = useState([]);
    const [psicologos, setPsicologos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingUsuario, setEditingUsuario] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const initialState = {
        usuario: '',
        senha: '',
        role: 'USER',
        id_psicologo: null,
        ativo: true
    };
    const [formData, setFormData] = useState(initialState);

    useEffect(() => {
        carregarUsuarios();
        carregarPsicologos();
    }, []);

    const toArray = (data) => Array.isArray(data) ? data : (data?.content ?? []);

    const carregarUsuarios = async () => {
        setLoading(true);
        try {
            const response = await api.get('/usuarios');
            setUsuarios(toArray(response.data));
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
            mostrarMensagem('Erro ao carregar usuários.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const carregarPsicologos = async () => {
        try {
            const response = await api.get('/psicologos');
            setPsicologos(toArray(response.data));
        } catch (error) {
            console.error('Erro ao carregar psicólogos:', error);
        }
    };

    const handleOpenDialog = (usuario = null) => {
        if (usuario) {
            setEditingUsuario(usuario);
            setFormData({
                usuario: usuario.usuario || '',
                senha: '', // Senha não é preenchida na edição
                role: usuario.usuario_role || 'USER',
                id_psicologo: usuario.psicologo?.id_psicologo || null,
                ativo: usuario.ativo !== undefined ? usuario.ativo : true
            });
        } else {
            setEditingUsuario(null);
            setFormData(initialState);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingUsuario(null);
    };

    const handleSave = async () => {
        if (!formData.usuario) {
            mostrarMensagem('Preencha o campo Usuário.', 'warning');
            return;
        }
        if (!editingUsuario && !formData.senha) {
            mostrarMensagem('Preencha o campo Senha para novos usuários.', 'warning');
            return;
        }

        try {
            if (editingUsuario) {
                await api.put(`/usuarios/${editingUsuario.id}`, formData, { skipGlobalError: true });
                toastSuccess('Usuário atualizado.');
            } else {
                await api.post('/usuarios', formData, { skipGlobalError: true });
                toastSuccess('Usuário criado.');
            }
            carregarUsuarios();
            handleCloseDialog();
        } catch (error) {
            // Login repetido chega como INTEGRIDADE, e o catálogo explica que o
            // registro já existe — antes era "Erro ao salvar usuário."
            mostrarErro(error, 'salvar o usuário');
        }
    };

    const handleDelete = async (id) => {
        const ok = await confirmAcao({
            title: 'Excluir usuário?',
            text: 'O acesso dele ao sistema é removido. Esta ação não pode ser desfeita.',
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/usuarios/${id}`, { skipGlobalError: true });
            toastSuccess('Usuário excluído.');
            carregarUsuarios();
        } catch (error) {
            mostrarErro(error, 'excluir o usuário');
        }
    };

    const mostrarMensagem = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    Usuários do Sistema
                </Typography>
                {!soVisualizacao && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Novo Usuário
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                <TableCell><strong>Usuário</strong></TableCell>
                                <TableCell><strong>Perfil</strong></TableCell>
                                <TableCell><strong>Psicólogo Vinculado</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Último IP</strong></TableCell>
                                <TableCell><strong>Último Login</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && usuarios.length > 0 ? usuarios.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{item.usuario}</TableCell>
                                    <TableCell>{item.usuario_role}</TableCell>
                                    <TableCell>{item.nome_psicologo || '-'}</TableCell>
                                    <TableCell>{item.ativo ? 'Ativo' : 'Inativo'}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.ultimoIp || '-'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                        {item.dataUltimoLogin ? new Date(item.dataUltimoLogin).toLocaleString('pt-BR') : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {!soVisualizacao && (
                                            <>
                                                <Tooltip title="Editar">
                                                    <IconButton onClick={() => handleOpenDialog(item)} size="small" color="primary"><EditIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton onClick={() => handleDelete(item.id)} size="small" color="error"><DeleteIcon /></IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{loading ? 'Carregando...' : 'Nenhum usuário encontrado.'}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3, pt: 1 }}>
                        <Box sx={{ gridColumn: 'span 12' }}>
                            <TextField fullWidth required label="Usuário (Login)" name="usuario" value={formData.usuario} onChange={(e) => setFormData({ ...formData, usuario: e.target.value })} />
                        </Box>
                        <Box sx={{ gridColumn: 'span 12' }}>
                            <TextField fullWidth type="password" label={editingUsuario ? "Nova Senha (deixe em branco para não alterar)" : "Senha"} name="senha" value={formData.senha} onChange={(e) => setFormData({ ...formData, senha: e.target.value })} />
                        </Box>
                        <Box sx={{ gridColumn: 'span 6' }}>
                            <FormControl fullWidth>
                                <InputLabel>Perfil</InputLabel>
                                <Select name="role" value={formData.role} label="Perfil" onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                                    <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                                    <MenuItem value="ROLE_USUARIO">Usuário</MenuItem>
                                    <MenuItem value="ROLE_VISUALIZADOR">Visualizador (somente leitura)</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ gridColumn: 'span 6' }}>
                            <FormControl fullWidth>
                                <InputLabel>Vincular Psicólogo</InputLabel>
                                <Select name="id_psicologo" value={formData.id_psicologo || ''} label="Vincular Psicólogo" onChange={(e) => setFormData({ ...formData, id_psicologo: e.target.value })}>
                                    <MenuItem value={null}><em>Nenhum</em></MenuItem>
                                    {psicologos.map(p => <MenuItem key={p.id_psicologo} value={p.id_psicologo}>{p.nomeCompleto}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ gridColumn: 'span 12' }}>
                             <FormControlLabel control={<Switch checked={formData.ativo} onChange={(e) => setFormData({...formData, ativo: e.target.checked})} />} label="Usuário Ativo" />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editingUsuario ? 'Salvar Alterações' : 'Criar Usuário'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
